import type { AppointmentInternalNote } from '../../services/aiSummaryService';
import type { Routemize_DTOs_Metadata_CreateNoteDTO } from '../../api/models/Routemize_DTOs_Metadata_CreateNoteDTO';
import type { Routemize_DTOs_Metadata_EntityNoteDTO } from '../../api/models/Routemize_DTOs_Metadata_EntityNoteDTO';
import type { Routemize_DTOs_Metadata_NotesBundleDTO } from '../../api/models/Routemize_DTOs_Metadata_NotesBundleDTO';
import { Routemize_Entities_Metadata_NotePriority as NotePriority } from '../../api/models/Routemize_Entities_Metadata_NotePriority';
import { Routemize_Entities_Metadata_NoteType as NoteType } from '../../api/models/Routemize_Entities_Metadata_NoteType';

/**
 * Pure helpers for the note kinds of the notes consolidation (86bbkaqna). The server classifies notes into a
 * {@link Routemize_DTOs_Metadata_NotesBundleDTO}; the client reads the bundle and never re-derives the kind.
 */

export type EntityNote = Routemize_DTOs_Metadata_EntityNoteDTO;
export type NotesBundle = Routemize_DTOs_Metadata_NotesBundleDTO;

/**
 * The bundle's Customer notes, oldest first (as the server returns them). Never `notes[0]` (D1). Not filtered by
 * type: the server's kind mapping also puts Request `General` and `CustomerFacing` notes in `customer`.
 */
export const customerNotes = (bundle: NotesBundle | null | undefined): EntityNote[] => bundle?.customer ?? [];

/** Display order: pinned first, then newest first. */
export const sortNotes = (list: EntityNote[] | null | undefined): EntityNote[] =>
  [...(list ?? [])].sort((a, b) => {
    if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
    return new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime();
  });

/** Note contents as one text block (AI context, previews). Blank notes are skipped. */
export const joinNoteContents = (list: EntityNote[] | null | undefined, separator = '\n\n'): string =>
  (list ?? []).map(n => n.content?.trim()).filter((c): c is string => !!c).join(separator);

/**
 * The appointment's own archive/cancel reasons, oldest first. `bundle.reasons` also carries the request's StatusReason
 * notes (e.g. "Request Archived"), which are not about the appointment.
 */
export const appointmentReasons = (bundle: NotesBundle | null | undefined): EntityNote[] =>
  (bundle?.reasons ?? []).filter(n => n.entityType === 'Appointment');

/** The appointment's cancellation reason: its newest non-blank StatusReason note, or null. */
export const cancellationNoteFromBundle = (bundle: NotesBundle | null | undefined): EntityNote | null =>
  appointmentReasons(bundle)
    .filter(n => !!n.content?.trim())
    .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())[0] ?? null;

/** A staff note written from the web app. The title is display-only (Decision B). */
export const INTERNAL_NOTE_DEFAULTS: Omit<Routemize_DTOs_Metadata_CreateNoteDTO, 'content'> = {
  type: NoteType.INTERNAL,
  priority: NotePriority.NORMAL,
  title: 'Internal Note',
};

/** A note on a contact (Decision J). */
export const CONTACT_NOTE_DEFAULTS: Omit<Routemize_DTOs_Metadata_CreateNoteDTO, 'content'> = {
  type: NoteType.GENERAL,
  priority: NotePriority.NORMAL,
};

/** Server cap on `statusReason` (`EntityNoteConsts.StatusReasonMaxLength`). */
export const STATUS_REASON_MAX_LENGTH = 188;

/**
 * Splits one free-text archive/cancel reason into the update DTO's `statusReason` / `statusReasonDetails` (D9).
 * The server stores the reason, a blank line, then the details. Text whose first line fits the cap keeps that line as
 * the reason and the remaining lines as details; a longer first line is cut (with "…") and the whole text goes in
 * details.
 */
export function toStatusReason(text: string | null | undefined): { statusReason?: string; statusReasonDetails?: string } {
  const trimmed = text?.trim();
  if (!trimmed) return {};
  const newline = trimmed.indexOf('\n');
  const firstLine = (newline >= 0 ? trimmed.slice(0, newline) : trimmed).trim();
  if (firstLine.length <= STATUS_REASON_MAX_LENGTH) {
    const rest = newline >= 0 ? trimmed.slice(newline + 1).trim() : '';
    return { statusReason: firstLine, statusReasonDetails: rest || undefined };
  }
  return {
    statusReason: `${firstLine.slice(0, STATUS_REASON_MAX_LENGTH - 1).trimEnd()}…`,
    statusReasonDetails: trimmed,
  };
}

/**
 * The cancellation modal's reason + optional notes as the update DTO's `statusReason` / `statusReasonDetails` (D9):
 * the reason is the statusReason (capped by {@link toStatusReason}) and the notes are the details.
 */
export function toCancelReason(
  reason: string | undefined,
  notes: string | undefined
): { statusReason?: string; statusReasonDetails?: string } {
  const fromReason = toStatusReason(reason);
  const details = notes?.trim();
  if (!fromReason.statusReason) return toStatusReason(details);
  if (!details) return fromReason;
  return {
    statusReason: fromReason.statusReason,
    statusReasonDetails: [fromReason.statusReasonDetails, details].filter(Boolean).join('\n\n'),
  };
}

/** Staff notes as AI-briefing input; the default "Internal Note" title is dropped as noise. */
export const toBriefingNotes = (list: EntityNote[] | null | undefined): AppointmentInternalNote[] =>
  (list ?? [])
    .filter(n => !!n.content?.trim())
    .map(n => ({
      ...(n.title && n.title !== INTERNAL_NOTE_DEFAULTS.title ? { title: n.title } : {}),
      content: n.content!.trim(),
    }));

/** The bundle's notes as AI context: customer text, pre-booking and appointment notes (never stop-label marker text, F2/D6). */
export const briefingNotesFromBundle = (bundle: NotesBundle | null | undefined) => ({
  customerNote: joinNoteContents(customerNotes(bundle)) || undefined,
  preBookingNotes: toBriefingNotes(bundle?.preBooking),
  internalNotes: toBriefingNotes(bundle?.appointment),
});

/** Request-card preview length; matches `EntityNoteConsts.RequestCardPreviewLength` on the server. */
export const REQUEST_CARD_PREVIEW_LENGTH = 120;

export interface RequestNoteSummary {
  noteCount: number;
  latestInternalNotePreview: string | null;
}

/**
 * The request card's note count and preview, from a request bundle. Matches `RequestSearchResponse.NoteCount` /
 * `LatestInternalNotePreview` (server `NoteQueryService.GetRequestNoteSummariesAsync`): Customer + pre-booking notes
 * (reasons excluded); the newest pre-booking note by creation time (pins ignored, unlike {@link sortNotes}),
 * whitespace collapsed, cut to 120 characters plus "…". Lets a card update itself after a note edit without a re-search.
 */
export function requestNoteSummary(bundle: NotesBundle | null | undefined): RequestNoteSummary {
  const customer = bundle?.customer ?? [];
  const preBooking = bundle?.preBooking ?? [];
  const time = (n: EntityNote) => new Date(n.createdAt ?? n.creationTime ?? 0).getTime();
  const newest = [...preBooking].sort((a, b) => time(b) - time(a))[0];
  const text = newest?.content?.trim().replace(/\s+/g, ' ') ?? '';
  return {
    noteCount: customer.length + preBooking.length,
    latestInternalNotePreview: !text
      ? null
      : text.length <= REQUEST_CARD_PREVIEW_LENGTH
        ? text
        : `${text.slice(0, REQUEST_CARD_PREVIEW_LENGTH)}…`,
  };
}
